import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useBooks } from '../context/BookContext';
import { useAuth } from '../context/AuthContext';
import { X, Trash2, Check, Clock, RotateCcw, Star, Trophy, Headphones, Book, Tablet, BookOpen, Sparkles, MapPin } from 'lucide-react';
import RankingFlow from './RankingFlow';
import BookCuration from './BookCuration';

const BOOKSHOP_AFFILIATE_ID = "125712";
const AUDIBLE_AFFILIATE_TAG = "dhowell02-20";

const getBookshopLink = (book) => {
  // Search by ISBN rather than deep-linking /a/{id}/{isbn}: the direct product
  // URL 404s when Bookshop does not carry that exact edition, while an ISBN
  // search still finds sibling editions and keeps affiliate attribution.
  const query = encodeURIComponent(
    book.isbn ? book.isbn : `${book.title} ${book.author || ""}`.trim()
  );
  return `https://bookshop.org/search?keywords=${query}&affiliate=${BOOKSHOP_AFFILIATE_ID}`;
};

const getAudibleLink = (book) => {
  // Audible does not reliably resolve editions by ISBN the way Bookshop does,
  // so this always searches by title+author.
  const query = encodeURIComponent(`${book.title} ${book.author || ""}`.trim());
  return `https://www.audible.com/search?keywords=${query}&tag=${AUDIBLE_AFFILIATE_TAG}`;
};


const BookDetailModal = ({ book, onClose }) => {
  const { updateBook, deleteBook, addLog } = useBooks();
  const { user } = useAuth();
  const [minutes, setMinutes] = useState(30);
  const [isLogging, setIsLogging] = useState(false);
  const [showRankPrompt, setShowRankPrompt] = useState(false);
  const [showRankingFlow, setShowRankingFlow] = useState(false);
  const [resolvedIsbn, setResolvedIsbn] = useState(book?.isbn || null);
  const [shelfLocationLabel, setShelfLocationLabel] = useState(null);
  const [myShelves, setMyShelves] = useState([]);
  const [assignedShelf, setAssignedShelf] = useState(null);
  const [curationBookId, setCurationBookId] = useState(book?.external_id || null);

  useEffect(() => {
    let cancelled = false;
    if (book && !book.isbn && book.title) {
      supabase
        .from('book_cache')
        .select('isbn')
        .ilike('title', book.title)
        .not('isbn', 'is', null)
        .limit(1)
        .then(({ data }) => {
          if (!cancelled && data && data.length > 0) {
            setResolvedIsbn(data[0].isbn);
          }
        });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book?.id]);

  // Shop Curation is keyed by book_cache.external_id (the shared,
  // cross-user book identity -- see BookCuration.js), not this personal
  // library row's id. Resolve it the same way resolvedIsbn is resolved
  // above, by matching title against the shared book_cache table.
  useEffect(() => {
    let cancelled = false;
    if (book?.external_id) {
      setCurationBookId(book.external_id);
      return;
    }
    if (book?.title) {
      supabase
        .from('book_cache')
        .select('external_id')
        .ilike('title', book.title)
        .limit(1)
        .then(({ data }) => {
          if (!cancelled && data && data.length > 0) {
            setCurationBookId(data[0].external_id);
          }
        });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book?.id, book?.title]);

  // The book's shelf (status tab: reading/tbr/read/owned) may have a
  // real-world location label set from the Library shelf view.
  useEffect(() => {
    let cancelled = false;
    setShelfLocationLabel(null);
    if (user && book?.status) {
      supabase
        .from('shelf_locations')
        .select('location_label')
        .eq('user_id', user.id)
        .eq('shelf_key', book.status)
        .maybeSingle()
        .then(({ data }) => {
          if (!cancelled && data?.location_label) {
            setShelfLocationLabel(data.location_label);
          }
        });
    }
    return () => { cancelled = true; };
  }, [user, book?.status]);

  // Real, named shelves (independent of status) -- load the user's list
  // for the assignment dropdown, and resolve which one (if any) this
  // book currently lives on.
  useEffect(() => {
    if (!user) return;
    supabase
      .from('shelves')
      .select('id, name, location_label')
      .eq('user_id', user.id)
      .order('sort_order', { ascending: true, nullsFirst: false })
      .then(({ data }) => setMyShelves(data || []));
  }, [user]);

  useEffect(() => {
    setAssignedShelf(myShelves.find(s => s.id === book?.shelf_id) || null);
  }, [myShelves, book?.shelf_id]);

  const handleShelfChange = async (e) => {
    const newShelfId = e.target.value || null;
    await updateBook(book.id, { shelf_id: newShelfId });
  };

  if (!book) return null;

  // Helpers
  const formatDuration = (mins) => {
    if (!mins) return 'Unknown length';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  };

  const FormatIcon = () => {
    switch (book.format) {
      case 'audio': return <Headphones size={14} />;
      case 'ebook': return <Tablet size={14} />;
      default: return <Book size={14} />;
    }
  };

  const handleStatusChange = async (newStatus) => {
    await updateBook(book.id, { status: newStatus });
    if (newStatus === 'read') {
      setShowRankPrompt(true);
    }
  };

  const handleRemove = async () => {
    if (window.confirm(`Are you sure you want to remove "${book.title}"?`)) {
      await deleteBook(book.id);
      onClose();
    }
  };

  const handleQuickLog = async (e) => {
    e.preventDefault();
    setIsLogging(true);
    try {
      await addLog({
        book_id: book.id,
        title: book.title,
        date: new Date().toISOString().split('T')[0],
        minutes: parseInt(minutes)
      });
      alert(`Logged ${minutes}m for ${book.title}!`);
      setMinutes(30);
    } catch (error) {
      console.error("Failed to log session", error);
    } finally {
      setIsLogging(false);
    }
  };

  if (showRankingFlow) {
    return <RankingFlow book={book} onClose={onClose} />;
  }

  if (showRankPrompt) {
    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <Trophy size={48} color="#ffc107" style={{ marginBottom: '1rem' }} />
            <h2>Congratulations!</h2>
            <p>You've finished <strong>{book.title}</strong>.</p>
            <p>Are you ready to find its place in your Hall of Fame?</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '2rem' }}>
              <button 
                onClick={() => setShowRankingFlow(true)}
                style={{ ...actionBtn, backgroundColor: '#333', color: 'white' }}
              >
                Rank it now
              </button>
              <button 
                onClick={onClose}
                style={{ ...actionBtn, border: '1px solid #ddd', color: '#666' }}
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3 style={{ margin: 0 }}>Book Details</h3>
          <button onClick={onClose} style={closeButtonStyle}><X size={20} /></button>
        </div>

        <div style={contentStyle}>
          <div style={heroSection}>
            {book.cover ? (
              <img src={book.cover} alt={book.title} style={coverStyle} />
            ) : (
              <div style={placeholderStyle}>{book.title[0]}</div>
            )}
            <div style={mainInfo}>
              <h2 style={titleStyle}>{book.title}</h2>
              <p style={authorStyle}>{book.author}</p>
              
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                <span style={statusBadgeStyle(book.status)}>{book.status === 'owned' ? 'IN COLLECTION' : book.status.toUpperCase()}</span>
                
                <span style={formatBadgeStyle}>
                  <FormatIcon />
                  {book.format === 'audio' ? 'Audiobook' : (book.format === 'ebook' ? 'E-Book' : 'Physical')}
                </span>

                <span style={metaTextStyle}>
                  {book.format === 'audio'
                    ? formatDuration(book.audio_minutes)
                    : `${book.totalPages || '?'} pages`}
                </span>
              </div>

              {assignedShelf ? (
                <div style={livesOnStyle}>
                  <MapPin size={13} />
                  <span>
                    Lives on: {assignedShelf.name}
                    {assignedShelf.location_label ? ` — ${assignedShelf.location_label}` : ''}
                  </span>
                </div>
              ) : shelfLocationLabel && (
                <div style={livesOnStyle}>
                  <MapPin size={13} />
                  <span>Lives on: {shelfLocationLabel}</span>
                </div>
              )}

              {myShelves.length > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '0.75rem', color: '#888' }}>Shelf:</span>
                  <select
                    value={book.shelf_id || ''}
                    onChange={handleShelfChange}
                    style={{ fontSize: '0.8rem', padding: '3px 6px', borderRadius: '6px', border: '1px solid #ccc' }}
                  >
                    <option value="">No shelf</option>
                    {myShelves.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div style={divider} />

          {/* Quick Log Section (Only if status is not TBR) */}
          {book.status !== 'tbr' && (
            <div style={sectionStyle}>
              <div style={sectionHeader}>
                <Clock size={18} color="#4caf50" />
                <h4 style={{ margin: 0 }}>Quick Log Session</h4>
              </div>
              <form onSubmit={handleQuickLog} style={logFormStyle}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input 
                    type="number" 
                    value={minutes} 
                    onChange={e => setMinutes(e.target.value)}
                    style={smallInputStyle}
                  />
                  <span style={{ fontSize: '0.9rem', color: '#666' }}>minutes</span>
                </div>
                <button type="submit" style={logButtonStyle} disabled={isLogging}>
                  {isLogging ? '...' : 'Log Session'}
                </button>
              </form>
            </div>
          )}

          {/* Cozy Affiliate Purchase Section (Only for TBR) */}
          {book.status === 'tbr' && (
            <div style={affiliateSectionStyle}>
              <div style={sectionHeader}>
                <Sparkles size={18} color="#ff9800" />
                <h4 style={{ margin: 0, color: '#5d4037' }}>Acquire a Copy</h4>
              </div>
              {book.format === 'audio' ? (
                <>
                  <p style={{ fontSize: '0.8rem', color: '#6d4c41', margin: '8px 0 12px 0', lineHeight: '1.4' }}>
                    Ready to turn this wishlist book into a listen? Grab it on Audible!
                  </p>
                  <a
                    href={getAudibleLink(book)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={bookshopButtonStyle}
                  >
                    🎧 Buy on Audible
                  </a>
                  <p style={{ fontSize: '0.7rem', color: '#a1887f', margin: '8px 0 0 0' }}>
                    Purchases support Leaf &amp; Letter.
                  </p>
                </>
              ) : (
                <>
                  <p style={{ fontSize: '0.8rem', color: '#6d4c41', margin: '8px 0 12px 0', lineHeight: '1.4' }}>
                    Ready to turn this wishlist book into a physical leaf? Cozy options below support both Leaf & Letter and local indie bookstores!
                  </p>
                  <a
                    href={getBookshopLink(book)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={bookshopButtonStyle}
                  >
                    📖 Buy on Bookshop.org
                  </a>
                  <p style={{ fontSize: '0.7rem', color: '#a1887f', margin: '8px 0 0 0' }}>
                    Purchases support indie bookstores &amp; Leaf &amp; Letter.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Book Vibe Section */}
          <div style={sectionStyle}>
            <div style={sectionHeader}>
              <Sparkles size={18} color="var(--accent)" />
              <h4 style={{ margin: 0 }}>Book Vibe (Reactive Weather)</h4>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
              {[
                { id: 'melancholy', label: 'Melancholy', emoji: '🌧️', activeColor: '#2196f3', activeSoft: 'var(--bg-app)' },
                { id: 'cozy', label: 'Cozy', emoji: '☀️', activeColor: '#ff9800', activeSoft: 'var(--bg-app)' },
                { id: 'mysterious', label: 'Mysterious', emoji: '🌫️', activeColor: '#9c27b0', activeSoft: 'var(--bg-app)' },
                { id: 'thrilling', label: 'Thrilling', emoji: '⚡', activeColor: '#f44336', activeSoft: 'var(--bg-app)' }
              ].map(v => {
                const isActive = book.vibe === v.id;
                return (
                  <button
                    key={v.id}
                    onClick={() => updateBook(book.id, { vibe: isActive ? null : v.id })}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      border: isActive ? `1px solid ${v.activeColor}` : '1px solid var(--border)',
                      backgroundColor: isActive ? v.activeSoft : 'var(--bg-card)',
                      color: isActive ? v.activeColor : 'var(--text-soft)',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: isActive ? 'bold' : 'normal',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span>{v.emoji}</span>
                    <span>{v.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={divider} />

          {/* Shop Curation Section */}
          {curationBookId && <BookCuration bookId={curationBookId} />}

          <div style={divider} />

          {/* Actions Section */}
          <div style={sectionStyle}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: '#666' }}>Actions</h4>
            <div style={actionGrid}>
              {book.status !== 'read' && (
                <button 
                  onClick={() => handleStatusChange('read')}
                  style={{ ...actionBtn, backgroundColor: '#4caf50', color: 'white' }}
                >
                  <Check size={18} /> Finished Reading
                </button>
              )}
              
              {book.status === 'tbr' && (
                <button 
                  onClick={() => handleStatusChange('reading')}
                  style={{ ...actionBtn, backgroundColor: '#2196f3', color: 'white' }}
                >
                  <Clock size={18} /> Start Reading
                </button>
              )}

              {book.status !== 'owned' && (
                <button 
                  onClick={() => handleStatusChange('owned')}
                  style={{ ...actionBtn, border: '1px solid #ddd', color: '#666' }}
                >
                  <BookOpen size={18} /> Move to Collection
                </button>
              )}

              {(book.status === 'read' || book.status === 'reading') && (
                <button 
                  onClick={() => handleStatusChange('tbr')}
                  style={{ ...actionBtn, border: '1px solid #ddd', color: '#666' }}
                >
                  <RotateCcw size={18} /> Move to TBR
                </button>
              )}

              {resolvedIsbn && (
                <a
                  href={`https://bookshop.org/a/${BOOKSHOP_AFFILIATE_ID}/${resolvedIsbn}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...actionBtn, backgroundColor: '#8d6e63', color: 'white', textDecoration: 'none' }}
                >
                  📖 Get It
                </a>
              )}

              <button 
                onClick={handleRemove}
                style={{ ...actionBtn, border: '1px solid #ff5252', color: '#ff5252' }}
              >
                <Trash2 size={18} /> Remove Book
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const starRatingStyle = {
  display: 'flex',
  gap: '2px',
  marginLeft: '8px'
};

// Styles
const overlayStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1200,
  padding: '1rem'
};

const modalStyle = {
  backgroundColor: 'var(--bg-card)',
  borderRadius: '16px',
  padding: '1.5rem',
  width: '100%',
  maxWidth: '400px',
  maxHeight: '90vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
  color: 'var(--text-main)'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1.5rem'
};

const closeButtonStyle = { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-soft)' };

const contentStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem'
};

const heroSection = {
  display: 'flex',
  gap: '1.2rem',
  alignItems: 'center'
};

const coverStyle = {
  width: '80px',
  height: '120px',
  objectFit: 'cover',
  borderRadius: '8px',
  boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
};

const placeholderStyle = {
  width: '80px',
  height: '120px',
  backgroundColor: 'var(--bg-app)',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '2rem',
  color: 'var(--text-soft)',
  border: '1px solid var(--border)'
};

const mainInfo = {
  flex: 1
};

const titleStyle = {
  margin: '0 0 4px 0',
  fontSize: '1.2rem',
  lineHeight: '1.2',
  color: 'var(--text-main)'
};

const authorStyle = {
  margin: '0 0 12px 0',
  color: 'var(--text-soft)',
  fontSize: '0.9rem'
};

const statusBadgeStyle = (status) => ({
  fontSize: '0.65rem',
  fontWeight: 'bold',
  padding: '4px 8px',
  borderRadius: '4px',
  backgroundColor: status === 'read' ? 'var(--accent-soft)' : (status === 'reading' ? '#e3f2fd' : (status === 'owned' ? '#f0f0f0' : '#fff3e0')),
  color: status === 'read' ? 'var(--accent)' : (status === 'reading' ? '#2196f3' : (status === 'owned' ? '#666' : '#ff9800')),
  display: 'inline-block'
});

const formatBadgeStyle = {
  fontSize: '0.65rem',
  padding: '4px 8px',
  borderRadius: '4px',
  backgroundColor: 'var(--bg-app)',
  border: '1px solid var(--border)',
  color: 'var(--text-soft)',
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
};

const metaTextStyle = {
  fontSize: '0.7rem',
  color: 'var(--text-soft)',
  fontStyle: 'italic'
};

const livesOnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  marginTop: '6px',
  fontSize: '0.75rem',
  color: 'var(--text-soft)'
};

const divider = { height: '1px', backgroundColor: 'var(--border)', width: '100%' };

const sectionStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px'
};

const sectionHeader = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '4px'
};

const logFormStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: 'var(--bg-app)',
  padding: '12px',
  borderRadius: '10px',
  border: '1px solid var(--border)'
};

const smallInputStyle = {
  width: '60px',
  padding: '6px',
  borderRadius: '4px',
  border: '1px solid var(--border)',
  textAlign: 'center',
  fontSize: '1rem',
  backgroundColor: 'var(--bg-card)',
  color: 'var(--text-main)'
};

const logButtonStyle = {
  backgroundColor: 'var(--accent)',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '6px',
  fontWeight: 'bold',
  cursor: 'pointer',
  fontSize: '0.85rem'
};

const actionGrid = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '10px'
};

const actionBtn = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '12px',
  borderRadius: '8px',
  border: 'none',
  fontWeight: '600',
  cursor: 'pointer',
  fontSize: '0.9rem',
  transition: 'opacity 0.2s'
};

const affiliateSectionStyle = {
  backgroundColor: '#fdfbf7',
  padding: '1rem',
  borderRadius: '12px',
  border: '1px solid #efebe9',
  marginTop: '10px',
  boxShadow: '0 2px 8px rgba(141, 110, 99, 0.04)'
};

const bookshopButtonStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 14px',
  backgroundColor: '#8d6e63',
  color: 'white',
  textDecoration: 'none',
  fontSize: '0.8rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  transition: 'background-color 0.2s',
  textAlign: 'center'
};


export default BookDetailModal;